import {
  ACTIVE_NAVIGATION_BUTTON_CLASSES,
  BREAK_NAVIGATION_ITEM_BASE_CLASSES,
  DISABLED_NAVIGATION_BUTTON_CLASSES,
  NAVIGATION_BUTTON_BASE_CLASSES,
  NAVIGATION_BUTTON_CLASS_BY_SIZE,
} from './Paginator.constants';
import type {
  BuildPaginationItemsArgs,
  CreateNavButtonClassArgs,
  NumericOption,
  PaginationItem,
  PaginatorSize,
} from './Paginator.types';

/**
 * Builds the ordered list of numbered-page and break items to render for the paginator.
 */
export const buildPaginationItems = ({
  currentPage,
  pageCount,
  pageRangeDisplayed,
  marginPagesDisplayed,
}: BuildPaginationItemsArgs): PaginationItem[] => {
  if (pageCount <= 0) {
    return [];
  }

  const pages = new Set<number>();
  const middleWindowSize = Math.min(pageRangeDisplayed, pageCount);
  const halfLeft = Math.floor((middleWindowSize - 1) / 2);
  const halfRight = middleWindowSize - halfLeft - 1;

  let middleStart = Math.max(currentPage - halfLeft, 1);
  let middleEnd = Math.min(currentPage + halfRight, pageCount);

  if (middleEnd - middleStart + 1 < middleWindowSize) {
    if (middleStart === 1) {
      middleEnd = Math.min(pageCount, middleStart + middleWindowSize - 1);
    } else if (middleEnd === pageCount) {
      middleStart = Math.max(1, pageCount - middleWindowSize + 1);
    }
  }

  for (let page = 1; page <= Math.min(marginPagesDisplayed, pageCount); page += 1) {
    pages.add(page);
  }

  for (let page = middleStart; page <= middleEnd; page += 1) {
    pages.add(page);
  }

  for (let page = Math.max(pageCount - marginPagesDisplayed + 1, 1); page <= pageCount; page += 1) {
    pages.add(page);
  }

  const sortedPages = [...pages].sort((left, right) => left - right);
  const items: PaginationItem[] = [];

  sortedPages.forEach((page, index) => {
    const previousPage = sortedPages[index - 1];
    if (previousPage) {
      const gap = page - previousPage;
      if (gap === 2) {
        items.push({ type: 'page', page: previousPage + 1 });
      } else if (gap > 2) {
        items.push({ type: 'break', key: `break-${previousPage}-${page}` });
      }
    }

    items.push({ type: 'page', page });
  });

  return items;
};

/**
 * Converts either a page count or a list of numeric values into Select-compatible numeric options.
 */
export const createNumericOptions = (countOrValues: number | number[]): NumericOption[] => {
  if (Array.isArray(countOrValues)) {
    return countOrValues.map((value) => ({
      label: value,
      value,
    }));
  }

  return Array.from({ length: Math.max(countOrValues, 0) }, (_, index) => {
    const value = index + 1;
    return { label: value, value };
  });
};

/**
 * Builds the Tailwind class string for a pagination button, including active and disabled variants.
 */
export const createNavButtonClass = (
  navButtonPaddingClass: string,
  size: PaginatorSize,
  { active = false, disabled = false, rounded = '' }: CreateNavButtonClassArgs,
) => {
  return [
    ...NAVIGATION_BUTTON_BASE_CLASSES,
    NAVIGATION_BUTTON_CLASS_BY_SIZE[size],
    navButtonPaddingClass,
    rounded,
    active ? ACTIVE_NAVIGATION_BUTTON_CLASSES.join(' ') : '',
    disabled ? DISABLED_NAVIGATION_BUTTON_CLASSES.join(' ') : '',
  ]
    .filter(Boolean)
    .join(' ');
};

/**
 * Builds the Tailwind class string used for a non-interactive pagination break item.
 */
export const createBreakClass = (navButtonPaddingClass: string) =>
  [...BREAK_NAVIGATION_ITEM_BASE_CLASSES, navButtonPaddingClass].filter(Boolean).join(' ');
